import { useCallback } from 'react';

import { useTranslation, Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import { clearDialogAlerts } from '@/redux/dialogAlerts/actions';
import { selectDialogAlerts } from '@/redux/dialogAlerts/selectors';
import { popModal, pushModal } from '@/redux/modals/actions';
import { removeAction } from '@/redux/nodes/actions';
import { Action, ReminderAction } from '@/shared/types/action';

const useDeleteAction = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dialogErrors = useSelector(selectDialogAlerts);

  // Delete a reminder action without removing the timer node it triggers.
  // Convergence (MOV-15429) means a single timer can be referenced by multiple
  // reminder actions; we never assume the timer is only used by THIS action.
  // The timer node persists as an orphan if no references remain, so the user
  // can re-attach to it later. They can manually delete the timer node from the
  // canvas if they want it gone.
  const deleteReminderAction = useCallback(
    (action: ReminderAction) => {
      dispatch(removeAction({ actionId: action.action_id }));
      if (dialogErrors.length) {
        dispatch(clearDialogAlerts({ id: action.action_id }));
      }
    },
    [dialogErrors.length, dispatch]
  );

  const deleteAction = useCallback(
    (action: Action) => {
      const type = action.type;
      const actionType = type === 'event' ? 'jump to' : type;
      const isReminderAction = type === 'reminder';
      const subtitle = isReminderAction ? (
        <Trans i18nKey="dialog.delete_reminder_action" />
      ) : (
        <Trans i18nKey="dialog.delete_action" values={[actionType]} />
      );

      const deleteProps = {
        subtitle,
        confirm: false,
        onDelete: () => {
          if (isReminderAction) {
            deleteReminderAction(action);
          } else {
            dispatch(removeAction({ actionId: action.action_id }));
            if (dialogErrors.length) {
              dispatch(clearDialogAlerts({ id: action.action_id }));
            }
          }

          dispatch(popModal());
        },
        secondaryButtonText: t('common.cancel'),
      };

      dispatch(pushModal(MODAL_DELETE, deleteProps));
    },
    [deleteReminderAction, dialogErrors.length, dispatch, t]
  );

  return { deleteAction, deleteReminderAction };
};

export default useDeleteAction;
